import axios from 'axios';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import AxiosErrorPage from '../features/ErrorPage/AxiosErrorPage';
import { useTemplate } from '../features/template/TemplateProvider';
import { getApiUrl } from '../utils/Env';
import { MainLoadingIcon } from '../utils/SVGIcon';
import { GetCurrentUrl } from '../utils/URL';

/**
 * @typedef {Object} InitInfo
 * @property {string} initId - The initialization ID stored in localStorage.
 * @property {Array} [currencyList] - List of available currencies.
 * @property {number} [status] - Status code (0 for success).
 */

/**
 * @typedef {Object} InitContextValue
 * @property {InitInfo|null} initInfo - The initialization data.
 * @property {Function} setInitInfo - Function to manually update `initInfo`.
 * @property {Function} initStateUpdated - Function to reload initialization state.
 * @property {Array} currencyList - List of currencies from the init data.
 */

const InitContext = createContext();
/**
 * Custom hook to access Init context
 * @returns {InitContextValue} The context value containing init data and utilities
 */
export const useInit = () => useContext(InitContext);

/**
 * Get saved authentication token from localStorage.
 * @returns {string|null} The stored token or null if not found.
 */
export const getToken = () => localStorage.getItem('token');

/**
 * Provides global initialization data for the app.
 *
 * - Fetches `/api/init` from backend on mount.
 * - Handles token validation and redirects to login if unauthorized.
 * - Displays loading or error state when needed.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - React child components.
 * @returns {JSX.Element} The provider wrapping its children, or loading/error state.
 */
function InitProvider({ children }) {
    const [initInfo, setInitInfo] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const { message, setMessage } = useTemplate();

    const setInit = (data) => {
        if (data?.status === 0) {
            // --Update Local Storage
            localStorage.setItem('init-id', data.data.initId);
            // --Update Store
            setInitInfo(data.data);
        } else {
            setInitInfo(data);
        }
    };

    useEffect(() => {
        setInit(null);
        axios
            .get(`${getApiUrl()}/api/init`, {
                headers: {
                    token: getToken()
                },
                timeout: 20000
            })
            .then(({ data }) => {
                setInit(data);
            })
            .catch((err) => {
                setInit(err);
                setMessage({
                    type: 'error',
                    text: `${err.name}: Something went wrong`
                });
            });
    }, [setMessage, reloadKey]);

    // --Update Local Storage
    useEffect(() => {
        const currentUrl = GetCurrentUrl();
        const localToken = () => localStorage.getItem('token');
        if (
            (message?.type === 'error' && message?.text.includes('Login Required')) ||
            message?.text.includes('Unauthorized') ||
            message?.text.includes('Invalid Token') ||
            message?.text.includes('Token Expired')
        ) {
            // remove token from local storage
            localToken();
            // got to login page
            if (!currentUrl.includes('login')) {
                window.location.href = `/login/?url=${currentUrl}`;
            } else {
                window.location.href = `/login/`;
            }
        }
    }, [message]);

    const value = useMemo(() => {
        const initStateUpdated = () => {
            setReloadKey((prev) => prev + 1);
        };
        return {
            initInfo,
            setInitInfo,
            initStateUpdated,
            currencyList: initInfo?.currencyList || []
        };
    }, [initInfo]);

    if (initInfo === null) {
        return (
            <div className="fixed h-28 w-28 bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="h-28 w-28 text-secondary">
                    <MainLoadingIcon />
                </div>
            </div>
        );
    }

    if (initInfo?.name === 'AxiosError') {
        return (
            <div className="w-screen h-screen">
                <AxiosErrorPage onClick={() => setReloadKey((d) => d + 1)} />
            </div>
        );
    }
    return <InitContext.Provider value={value}>{children}</InitContext.Provider>;
}

export default InitProvider;
